import { onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useScriptTag } from '@vueuse/core';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import { useEnvProps } from '~/composables/useEnvProps';
import { fireLinkTag } from '../../../features/analytics/tagManager';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';

interface Findation {
  init?: (button: HTMLButtonElement, clientId: string | undefined, options: Record<string, unknown>) => void;
  widgetDidOpen?: () => void;
  onSearchComplete?: (data: { matchSuccess: boolean; recommendedShade: string }) => void;
  widgetEmbedded?: () => void;
}

declare global {
  interface Window {
    Findation?: Findation;
  }
}

function useFindation(findationCallback: (matchingColor: string) => void) {
  const url = 'https://assets.findation.com/javascripts/w-adv-6.min.js';
  const linkName = 'pdp_find_your_shade';
  const clientId = useEnvProps().findationClientId;

  const consolidatedStore = useConsolidatedMemberStore();
  const { memberFlags, selectedMember, memberFeatureEligible, memberProduct } = storeToRefs(consolidatedStore);

  const { isLuxury, isFindationEnabled, isShopTheSetExpEnabled } = useFeatureEligibility();
  const { isMasterProductFlag } = useProductFlags();

  let callbackHandler: ((e: Event) => void) | null = null;
  let embeddedHandler: (() => void) | null = null;

  const isFindationApplicable = computed(() => {
    if (isShopTheSetExpEnabled) {
      return memberFlags.value?.isFindationEnabledFlag && !memberFeatureEligible.value?.isLuxury;
    }
    return isFindationEnabled && !isLuxury && !isMasterProductFlag;
  });

  function launchWidget(button: HTMLButtonElement) {
    window.Findation = window.Findation || {};
    const options = {
      ...(isShopTheSetExpEnabled && {
        productUrl: `${window.location.origin}${memberProduct.value?.identifier?.productUrl}`,
      }),
    };
    window.Findation.init?.(button, clientId, options);
  }

  function registerEventListeners(button: HTMLButtonElement) {
    if (!window.Findation) return;

    window.Findation.widgetDidOpen = () => {
      const event = {
        link_name: linkName,
      };
      fireLinkTag(event);
      document.getElementById('findation-iframe')?.focus();
    };
    window.Findation.onSearchComplete = (data) => {
      if (data.matchSuccess) {
        window.dispatchEvent(
          new CustomEvent('findationcallback', {
            detail: data.recommendedShade,
          }),
        );
      }
    };
    window.Findation.widgetEmbedded = () => {
      window.dispatchEvent(new CustomEvent('findationEmbedded', {}));
    };

    if (callbackHandler) {
      window.removeEventListener('findationcallback', callbackHandler);
    }
    if (embeddedHandler) {
      window.removeEventListener('findationEmbedded', embeddedHandler);
    }

    callbackHandler = (e: Event) => {
      const customEvent = e as CustomEvent<string>;
      findationCallback(customEvent.detail);
    };
    embeddedHandler = () => {
      const findationElement = button?.closest('[data-el="findation"]');

      if (findationElement) {
        findationElement.classList.remove('opacity-z');
      }
    };

    window.addEventListener('findationcallback', callbackHandler);
    window.addEventListener('findationEmbedded', embeddedHandler);
  }

  function initFindation(button: HTMLButtonElement | null) {
    if (!button) return;

    const launch = () => {
      launchWidget(button);
      registerEventListeners(button);
    };

    if (window.Findation?.init) {
      launch();
      return;
    }

    const { load } = useScriptTag(url, launch, { manual: true });
    load();
  }

  watch(selectedMember, () => {
    const existingButton = document.getElementById('findation-button-iframe') as HTMLIFrameElement | null;
    if (existingButton && isShopTheSetExpEnabled) {
      const newHref = encodeURIComponent(`${window.location.origin}${memberProduct.value?.identifier?.productUrl}`);
      existingButton.src = existingButton.src.replace(/href=[^&]*/, `href=${newHref}`);
    }
  });

  onUnmounted(() => {
    if (callbackHandler) {
      window.removeEventListener('findationcallback', callbackHandler);
    }
    if (embeddedHandler) {
      window.removeEventListener('findationEmbedded', embeddedHandler);
    }
  });

  return {
    initFindation,
    isFindationApplicable,
  };
}

export default useFindation;
